import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useStore, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  app,
  inspection,
  getPageLoadedSelector,
  getExcludedPage,
  getShowDifferenceBoxes,
  getSinglePageView,
  getHideDisplayPanels,
} from 'store';
import { DocumentType, DocumentStates, DocumentTypes, PDFTRON_DEFAULT_TOOL, StoreState } from 'types';
import {
  getInspectionId,
  getTextMatchJobId,
  getTextMatchCdnPathPrefix,
  getTextMatchMetadata,
} from 'store/inspection/inspectionSelectors';
import { isInspectionUrl, isResultUrl } from 'utils/location';
import { getStyleVariables } from 'styles/vars';
import WebViewer, { Core, WebViewerInstance } from '@pdftron/webviewer';
import { actions } from 'pdftron/docManager/reducer';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import {
  handleAnnotationSelected,
  handleAnnotationDeselected,
  handleAnnotationChanged,
  handleTextMatchClick,
} from '../../components/PDFViewer/EventHandlers';
import PDFAnnotationManager from '../../components/PDFViewer/PDFAnnotationManager';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import { getBackendURL } from 'store/interceptors';
import { AnnotationAction } from 'components/PDFViewer/Utils';
import { PDFTRON_PATH } from 'pdftron/pdftronConfig';
import { getWebViewerElement } from 'pdftron/utils/WebViewerUtils';
import { isWebViewerEngineError, reportWebViewerError } from 'pdftron/utils/webViewerError';
import { applyRenderSettingsAtInit } from 'pdftron/debug/applyRenderSettings';
import { registerAnchorResizeTool } from './AnnotationTools/AnchorResizeTool';
import { throttle } from 'lodash';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    pdfViewer: styleVariables.pdfViewer,
  };
});

// Scroll padding to mitigate aggressive scrolling with gesture mice in single page mode
const SCROLL_PADDING_PX = 126;

// visiblePagesChanged fires on every scroll tick; throttle the lazy overlay fill-in so a fast
// scroll through a large group doesn't queue a draw per frame.
const VISIBLE_PAGES_OVERLAY_THROTTLE_MS = 100;

interface PDFViewerProps {
  file: string;
  document: DocumentType;
  fileId: string;
}

const PDFViewer = (props: PDFViewerProps) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const store = useStore();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const hasLoaded = useSelector(getPageLoadedSelector)[props.document];
  const showDifferenceBoxes = useSelector(getShowDifferenceBoxes);
  const singlePageView = useSelector(getSinglePageView);
  const location = useLocation();
  const getExcludedPages = useCallback((): Array<number> => {
    const state = store.getState() as StoreState;
    const excludedPage = getExcludedPage(state)[props.document];
    return excludedPage;
  }, [props.document, store]);
  const [filePath, setFilePath] = useState<string>();
  const url = getBackendURL();
  const instanceRef = useRef<WebViewerInstance | null>(null);
  const lastScrollLeftRef = useRef<number>(0);
  const isPageChangingRef = useRef<boolean>(false);
  const scrollListenerRef = useRef<(() => void) | null>(null);
  const textMatchCleanupRef = useRef<(() => void) | null>(null);
  const webViewerErrorCleanupRef = useRef<(() => void) | null>(null);
  const visiblePagesCleanupRef = useRef<(() => void) | null>(null);

  const overrideMouseLeftDown = useCallback(
    function override(
      this:
        | Core.Tools.TextUnderlineCreateTool
        | Core.Tools.TextStrikeoutCreateTool
        | Core.Tools.TextHighlightCreateTool
        | Core.Tools.RectangleCreateTool,
      e: MouseEvent,
      excludedPages: Array<number>,
      mouseLeftDown: (e: MouseEvent) => void,
    ) {
      const target = e.target as HTMLDivElement;
      const pageNum = parseInt(target.id.replace('pageWidgetContainer', ''), 10);
      // if page number is in excludedPages array don't call original mouseLeftDown handler.
      if (excludedPages.indexOf(pageNum) > -1 && excludedPages.length !== 0) {
        dispatch(app.actions.setSnackMessage({ message: 'This page has been excluded', type: 'error' }));
      } else {
        mouseLeftDown.apply(this, [e]);
      }
    },
    [dispatch],
  );

  const overrideKeyDown = function override(
    this:
      | Core.Tools.TextUnderlineCreateTool
      | Core.Tools.TextStrikeoutCreateTool
      | Core.Tools.TextHighlightCreateTool
      | Core.Tools.RectangleCreateTool,
    e: React.KeyboardEvent,
  ) {
    e.preventDefault();
  };

  /** Resolve a file reference to a loadable URL.
   *  Signed URLs (https://) are used directly; legacy relative paths are
   *  prepended with the backend URL and the filename is encoded. */
  const resolveFileUrl = useCallback(
    (file: string): string => {
      if (file.startsWith('https://') || file.startsWith('http://')) {
        return file;
      }
      const parts = file.split('/');
      parts[parts.length - 1] = encodeURIComponent(parts[parts.length - 1]);
      return `${url}${parts.join('/')}`;
    },
    [url],
  );

  // Document is loading
  useEffect(() => {
    dispatch(
      inspection.actions.setLoadingState({
        documentType: props.document,
        documentState: DocumentStates.LOADING,
      }),
    );
  }, [dispatch, props.document]);

  useEffect(() => {
    if (props.file) {
      setFilePath(resolveFileUrl(props.file));
    }
  }, [props.file, resolveFileUrl]);

  useEffect(() => {
    if (filePath) {
      const instance = PDFManagerFactory.getViewer(props.document)?.instance;
      if (instance) {
        dispatch(
          inspection.actions.setLoadingState({
            documentType: props.document,
            documentState: DocumentStates.LOADING,
          }),
        );
        instance.UI.loadDocument(filePath);
        dispatch(actions.replaceFileId({ documentType: props.document, fileId: props.fileId }));
      }
    }
  }, [filePath, props.document]);

  useEffect(() => {
    const initialFilePath = resolveFileUrl(props.file);

    // The viewer never started (bad license key, WASM/worker assets failed to load,
    // unsupported browser). Without this the panel would stay silently blank.
    const handleInitError = (error: unknown) => {
      reportWebViewerError({ kind: 'init', documentType: props.document, error });
      // Nothing else will ever move this off LOADING, which would leave things
      // spinning forever. The toast is the explanation; this just releases the gated UI.
      dispatch(
        inspection.actions.setLoadingState({
          documentType: props.document,
          documentState: DocumentStates.LOADED,
        }),
      );
    };

    WebViewer.Iframe(
      {
        licenseKey: import.meta.env.VITE_PDFTRON_LICENSE_KEY,
        path: PDFTRON_PATH,
        initialDoc: initialFilePath,
        useDownloader: false,
        fullAPI: true,
        css: '/pdfTronStyles.css',
        disabledElements: [
          'default-top-header',
          'page-nav-floating-header',
          'tools-header',
          'toolStylePopup',
          'stylePopup',
          'textPopup',
          'contextMenuPopup',
          'viewControlsOverlay',
          'menuOverlay',
          'toolsOverlay',
          'pageNavOverlay',
          'linkButton',
          'annotationCommentButton',
          'annotationStyleEditButton',
          'annotationRedactButton',
          'annotationCropButton',
          'annotationGroupButton',
          'annotationUngroupButton',
          'annotationDeleteButton',
        ],
      },
      viewerRef.current as HTMLDivElement,
    )
      .then((instance: WebViewerInstance) => {
        instanceRef.current = instance;
        const { documentViewer, annotationManager } = instance.Core;

        applyRenderSettingsAtInit(instance.Core);

        // Surface WASM engine traps. PDFTron runs in an iframe, so these surface on the iframe
        // window rather than the parent. Best-effort: PDFTron sometimes swallows traps
        // internally so they never reach here.
        const iframeWindow = instance.UI.iframeWindow;
        if (iframeWindow) {
          const handleEngineError = (event: ErrorEvent) => {
            if (isWebViewerEngineError(event.message)) {
              reportWebViewerError({
                kind: 'crash',
                documentType: props.document,
                error: event.error,
                message: event.message,
              });
            }
          };
          const handleEngineRejection = (event: PromiseRejectionEvent) => {
            const message = event.reason instanceof Error ? event.reason.message : String(event.reason ?? '');
            if (isWebViewerEngineError(message)) {
              reportWebViewerError({ kind: 'crash', documentType: props.document, error: event.reason, message });
            }
          };
          iframeWindow.addEventListener('error', handleEngineError);
          iframeWindow.addEventListener('unhandledrejection', handleEngineRejection);
          webViewerErrorCleanupRef.current = () => {
            iframeWindow.removeEventListener('error', handleEngineError);
            iframeWindow.removeEventListener('unhandledrejection', handleEngineRejection);
          };
        }

        // Disable link generation so that they no longer get created/turned into annotations.
        documentViewer.disableAutomaticLinking();

        // Disable drag-to-scroll during annotation movement
        instance.Core.Tools.AnnotationSelectTool.disableDragScroll();

        dispatch(actions.loadWebViewer({ documentType: props.document, instance, fileId: props.fileId }));
        instance.UI.setTheme('dark');

        // Set default tool to Pan
        instance.UI.setToolMode(PDFTRON_DEFAULT_TOOL);

        instance.UI.setLayoutMode(instance.UI.LayoutMode.Continuous);

        // Add scroll padding to mitigate aggressive scrolling with gesture mice
        // This gives users breathing room at the top and bottom of pages, making scrolling less aggressive
        const scroller = documentViewer.getScrollViewElement() as HTMLElement;
        if (scroller) {
          scroller.style.paddingTop = `${SCROLL_PADDING_PX}px`;
          scroller.style.paddingBottom = `${SCROLL_PADDING_PX}px`;
          // If the viewer uses CSS scroll snapping, this helps the snap target
          // respect your buffer during wheel/keyboard scrolls:
          scroller.style.scrollPaddingTop = `${SCROLL_PADDING_PX}px`;
          scroller.style.scrollPaddingBottom = `${SCROLL_PADDING_PX}px`;
          // Let WebViewer re-measure after DOM/style changes
          documentViewer.scrollViewUpdated();
        }

        const strikeoutMouseLeftDown = instance.Core.Tools.TextStrikeoutCreateTool.prototype.mouseLeftDown;
        // eslint-disable-next-line no-param-reassign
        instance.Core.Tools.TextStrikeoutCreateTool.prototype.mouseLeftDown = function overrideStrikeOut(e: any) {
          overrideMouseLeftDown.call(this, e, getExcludedPages(), strikeoutMouseLeftDown);
        };
        const zoneMouseLeftDown = instance.Core.Tools.TextHighlightCreateTool.prototype.mouseLeftDown;
        // eslint-disable-next-line no-param-reassign
        instance.Core.Tools.TextHighlightCreateTool.prototype.mouseLeftDown = function overrideZone(e: any) {
          overrideMouseLeftDown.call(this, e, getExcludedPages(), zoneMouseLeftDown);
        };

        const graphicZoneMouseLeftDown = instance.Core.Tools.RectangleCreateTool.prototype.mouseLeftDown;
        // eslint-disable-next-line no-param-reassign
        instance.Core.Tools.RectangleCreateTool.prototype.mouseLeftDown = function overrideZone(e: any) {
          overrideMouseLeftDown.call(this, e, getExcludedPages(), graphicZoneMouseLeftDown);
        };

        // Register anchor-locked resize tool
        registerAnchorResizeTool(instance);

        // disable all the key down events(hotkeys) on annotations
        // eslint-disable-next-line no-param-reassign
        instance.Core.Tools.TextStrikeoutCreateTool.prototype.keyDown = function overrideStrikeOutKeyDown(e: any) {
          overrideKeyDown.call(this, e);
        };

        // eslint-disable-next-line no-param-reassign
        instance.Core.Tools.TextHighlightCreateTool.prototype.keyDown = function overrideStrikeOutKeyDown(e: any) {
          overrideKeyDown.call(this, e);
        };

        // eslint-disable-next-line no-param-reassign
        instance.Core.Tools.RectangleCreateTool.prototype.keyDown = function overrideStrikeOutKeyDown(e: any) {
          overrideKeyDown.call(this, e);
        };

        getWebViewerElement(props.document)?.setAttribute('tabindex', '0'); // adding tabindex to make the webviewer element focusable

        // Disable all PDFTron hotkeys first, then explicitly re-enable only page navigation keys.
        instance.UI.hotkeys.off();
        instance.UI.hotkeys.on(instance.UI.hotkeys.Keys.SPACE);
        instance.UI.hotkeys.on(instance.UI.hotkeys.Keys.UP);
        instance.UI.hotkeys.on(instance.UI.hotkeys.Keys.DOWN);

        const getAnnotationCommentElement = (annotation: Core.Annotations.Annotation) => {
          const comment = annotation.getContents();
          const user = annotation.Author;
          const date = annotation.DateModified;

          let dateModified;
          if (date) {
            const dateFormatted = date.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
            const time = date.toLocaleString('en-US', {
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
            });
            dateModified = `${dateFormatted} | ${time}`;
          } else {
            dateModified = `N/A`;
          }

          const commentContainer = document.createElement('div');
          const authorDiv = document.createElement('div');
          // hard coded css. Will have to adjust multiple areas if mockup changes
          authorDiv.style.cssText =
            'font-style: normal; font-weight: 600; font-size: 11px; line-height: 15px; padding-bottom: 8px;';
          authorDiv.appendChild(document.createTextNode(`${user || 'Author'}`));
          const commentDiv = document.createElement('div');
          commentDiv.style.cssText =
            'font-style: normal; font-weight: normal; font-size: 11px; line-height: 15px; color: rgba(255, 255, 255, 0.87); padding-bottom: 8px';
          commentDiv.appendChild(document.createTextNode(`${comment || 'N/A'}`));
          const dateDiv = document.createElement('div');
          dateDiv.style.cssText =
            'font-style: normal; font-weight: normal; font-size: 10px; line-height: 14px; color: rgba(255, 255, 255, 0.6);';
          dateDiv.appendChild(document.createTextNode(dateModified));
          commentContainer.appendChild(authorDiv);
          commentContainer.appendChild(commentDiv);
          commentContainer.appendChild(dateDiv);
          commentContainer.style.cssText = 'padding: 16px;';
          return commentContainer;
        };

        const getDividerElement = () => {
          const hr = document.createElement('hr');
          hr.style.backgroundColor = 'black';
          hr.style.height = '1px';
          hr.style.border = 'none';
          hr.style.margin = '0px';
          return hr;
        };

        // To show overlays only to a non-GV/internal annotation
        instance.UI.setAnnotationContentOverlayHandler((annotation: Core.Annotations.Annotation) => {
          if (!PDFManagerFactory.getPDFDocManager()?.isGVAnnotation(annotation) && annotation.getContents() !== '') {
            let annotationArray: Core.Annotations.Annotation[] = [annotation, ...annotation.getReplies()];
            annotationArray.sort(
              (comment1, comment2) =>
                new Date(comment1.DateModified).getTime() - new Date(comment2.DateModified).getTime(),
            );
            if (annotationArray.length > 3) {
              annotationArray = annotationArray.slice(0, 3);
            }
            const container = document.createElement('div');
            annotationArray.forEach((annot, index, array) => {
              const annotationComment = getAnnotationCommentElement(annot);
              container.appendChild(annotationComment);
              if (index + 1 < array.length) {
                container.appendChild(getDividerElement());
              }
            });
            if ([annotation, ...annotation.getReplies()].length > 3) {
              const numReplies = document.createTextNode(
                `+ ${[annotation, ...annotation.getReplies()].length - 3} replies`,
              );
              const numRepliesDiv = document.createElement('div');
              numRepliesDiv.style.cssText =
                'font-style: normal; font-weight: normal; font-size: 11px; line-height: 15px; padding-left: 16px; padding-bottom: 16px;';
              numRepliesDiv.appendChild(numReplies);
              container.appendChild(numRepliesDiv);
            }
            // container has negative margin to cover the change in background color compared to its parent the div AnnotationsContentOverlay
            // the parent container has a max-width of 215px, -32px for the 16px padding on each side. 183px width left.
            container.style.cssText =
              'max-width: 183px; min-width: 183px; background: #2D2F34; box-shadow: 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 5px 5px -3px rgba(0, 0, 0, 0.2); border-radius: 3px; margin: -8px;';
            return container;
          }
          return false;
        });

        annotationManager.addEventListener(
          'annotationSelected',
          (annotations: Core.Annotations.Annotation[], action: any) => {
            if (action === AnnotationAction.SELECTED && annotations[0].getCustomData('liveText') !== 'true') {
              handleAnnotationSelected(annotations, store, props.document);
            } else {
              handleAnnotationDeselected(annotations[0], props.document);
            }
          },
        );

        // Track scroll position to preserve horizontal scroll when changing pages
        const scrollElement = documentViewer.getScrollViewElement() as HTMLElement;
        if (scrollElement) {
          const handleScroll = () => {
            // Don't update scroll position during page changes (PDFTron resets it)
            if (!isPageChangingRef.current) {
              lastScrollLeftRef.current = scrollElement.scrollLeft;
            }
          };
          scrollListenerRef.current = handleScroll;
          scrollElement.addEventListener('scroll', handleScroll);
        }

        // @pdftron-refactor Move to saga
        documentViewer.addEventListener('pageNumberUpdated', (pageNumber: number) => {
          // Mark that we're changing pages to prevent scroll listener from updating
          isPageChangingRef.current = true;

          dispatch(
            inspection.actions.setCurrentPage({
              documentType: props.document,
              currentPage: pageNumber,
            }),
          );

          // Restore horizontal scroll position after PDFTron resets it
          // Use setTimeout to let PDFTron finish its scroll reset first
          setTimeout(() => {
            const scrollerElement = documentViewer.getScrollViewElement() as HTMLElement;
            if (scrollerElement) {
              // Always restore, even if scrollLeft was 0 (PDFTron might reset to a small non-zero value)
              scrollerElement.scrollLeft = lastScrollLeftRef.current;
            }
            // Re-enable scroll tracking after restoration
            isPageChangingRef.current = false;
          }, 0);
        });

        // Lazily draw the focused group's high-contrast overlay for pages entering the viewport.
        // Groups can span many pages; drawing them all up front on focus is wasteful, so the focus
        // saga draws the focused page immediately and the rest are filled in here as they scroll into
        // view. Throttled because visiblePagesChanged fires rapidly during scroll; no-ops when nothing
        // is focused, and skips pages that already have a matching overlay.
        const drawVisibleGroupOverlays = throttle((visiblePages: number[]) => {
          PDFManagerFactory.getPDFDocManager()?.drawVisibleGroupHighContrast(props.document, visiblePages);
        }, VISIBLE_PAGES_OVERLAY_THROTTLE_MS);
        documentViewer.addEventListener('visiblePagesChanged', drawVisibleGroupOverlays);
        visiblePagesCleanupRef.current = () => {
          drawVisibleGroupOverlays.cancel();
          documentViewer.removeEventListener('visiblePagesChanged', drawVisibleGroupOverlays);
        };

        instance.Core.Annotations.SelectionModel.useDashedLine = !showDifferenceBoxes;
        instance.Core.Annotations.SelectionModel.showPaddingWhenAnnotationIsSmall = false;
        instance.Core.Annotations.SelectionModel.selectionOutlineExtraPadding = 0;
        instance.Core.Annotations.SelectionModel.defaultNoPermissionSelectionOutlineColor =
          new instance.Core.Annotations.Color(0, 0, 0, 0); // no default PDFTron outline on differences

        // Listen for new annotations
        // @pdftron-refactor Move to saga
        documentViewer
          .getAnnotationManager()
          .addEventListener(
            'annotationChanged' as unknown as any,
            (annotations: Core.Annotations.Annotation[], action: string) => {
              handleAnnotationChanged(dispatch, props.document, annotations, action, store);
            },
          );

        // Listen for Ctrl/Cmd+Click for text match navigation
        const viewerElement = documentViewer.getViewerElement();
        viewerElement.addEventListener('click', (event: Event) => {
          const state = store.getState() as StoreState;
          const inspectionId = getInspectionId(state);
          const textMatchJobId = getTextMatchJobId(state);
          const cdnPathPrefix = getTextMatchCdnPathPrefix(state);
          const hideDisplayPanels = getHideDisplayPanels(state);
          const areBothPanelsVisible = !hideDisplayPanels.source && !hideDisplayPanels.target;
          if (inspectionId && textMatchJobId && areBothPanelsVisible) {
            handleTextMatchClick(
              event as MouseEvent,
              props.document,
              instance,
              inspectionId,
              textMatchJobId,
              cdnPathPrefix,
            );
          }
        });

        // Change cursor when Ctrl/Cmd is held to indicate text match navigation is available.
        // PDFTron renders inside an iframe, so we inject a <style> into the iframe's document.
        const iframeDoc = instance.UI.iframeWindow?.document;
        if (iframeDoc) {
          const cursorStyleId = `textMatchCursor-${props.document}`;

          const setCrosshairCursor = () => {
            if (!iframeDoc.getElementById(cursorStyleId)) {
              const style = iframeDoc.createElement('style');
              style.id = cursorStyleId;
              style.textContent = '* { cursor: pointer !important; }';
              iframeDoc.head.appendChild(style);
            }
          };
          const removeCrosshairCursor = () => {
            iframeDoc.getElementById(cursorStyleId)?.remove();
          };

          const handleKeyDown = (event: KeyboardEvent) => {
            if (!(event.ctrlKey || event.metaKey) || !isResultUrl()) return;
            const state = store.getState() as StoreState;
            const hideDisplayPanels = getHideDisplayPanels(state);
            if (hideDisplayPanels.source || hideDisplayPanels.target) return;
            const jobId = getTextMatchJobId(state);
            if (!jobId) return;
            const meta = getTextMatchMetadata(state);
            if (meta) setCrosshairCursor();
          };
          const handleKeyUp = (event: KeyboardEvent) => {
            if (!event.ctrlKey && !event.metaKey) {
              removeCrosshairCursor();
            }
          };
          const handleBlur = () => removeCrosshairCursor();

          // Listen on both the main document and the iframe document
          // (keydown fires on whichever has focus)
          iframeDoc.addEventListener('keydown', handleKeyDown);
          iframeDoc.addEventListener('keyup', handleKeyUp);
          document.addEventListener('keydown', handleKeyDown);
          document.addEventListener('keyup', handleKeyUp);
          window.addEventListener('blur', handleBlur);

          textMatchCleanupRef.current = () => {
            removeCrosshairCursor();
            iframeDoc.removeEventListener('keydown', handleKeyDown);
            iframeDoc.removeEventListener('keyup', handleKeyUp);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
          };
        }
      })
      .catch(handleInitError);

    return () => {
      // Clean up scroll event listener
      if (instanceRef.current && scrollListenerRef.current) {
        const documentViewer = instanceRef.current.Core.documentViewer;
        const scrollElement = documentViewer.getScrollViewElement() as HTMLElement;
        if (scrollElement) {
          scrollElement.removeEventListener('scroll', scrollListenerRef.current);
          scrollListenerRef.current = null;
        }
      }
      // Clean up text match cursor listeners
      textMatchCleanupRef.current?.();
      textMatchCleanupRef.current = null;

      // Clean up WebViewer crash listeners
      webViewerErrorCleanupRef.current?.();
      webViewerErrorCleanupRef.current = null;

      // Clean up the lazy overlay visible-pages listener
      visiblePagesCleanupRef.current?.();
      visiblePagesCleanupRef.current = null;

      instanceRef.current = null;
      dispatch(actions.unloadViewers(props.document));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply layout mode on flag changes
  useEffect(() => {
    if (!instanceRef.current) return;
    const { LayoutMode } = instanceRef.current.UI as any;
    if (LayoutMode && typeof instanceRef.current.UI.setLayoutMode === 'function') {
      instanceRef.current.UI.setLayoutMode(singlePageView ? LayoutMode.Single : LayoutMode.Continuous);

      // Apply or remove scroll padding based on view mode
      // Padding helps mitigate aggressive scrolling with gesture mice in single page mode
      const documentViewer = instanceRef.current.Core.documentViewer;
      const scroller = documentViewer.getScrollViewElement() as HTMLElement;
      if (scroller) {
        if (singlePageView) {
          // Apply padding for single page mode
          scroller.style.paddingTop = `${SCROLL_PADDING_PX}px`;
          scroller.style.paddingBottom = `${SCROLL_PADDING_PX}px`;
          scroller.style.scrollPaddingTop = `${SCROLL_PADDING_PX}px`;
          scroller.style.scrollPaddingBottom = `${SCROLL_PADDING_PX}px`;
        } else {
          // Remove padding for continuous mode
          scroller.style.paddingTop = '0px';
          scroller.style.paddingBottom = '0px';
          scroller.style.scrollPaddingTop = '0px';
          scroller.style.scrollPaddingBottom = '0px';
        }
        documentViewer.scrollViewUpdated();
      }
    }
  }, [singlePageView, props.document]);

  useEffect(() => {
    // deselect annotations when user clicks outside
    const handleClickOutside = (e: any) => {
      if (!isInspectionUrl(location)) {
        if (!viewerRef.current?.contains(e.target)) {
          if (props.document === DocumentTypes.source) {
            PDFAnnotationManager.getInstance(DocumentTypes.source)?.deselectAllAnnotations();
          } else {
            PDFAnnotationManager.getInstance(DocumentTypes.target)?.deselectAllAnnotations();
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hasLoaded, props.document]);

  return <div data-testid={`pdf_viewer_${props.document}`} className={classes.pdfViewer} ref={viewerRef} />;
};
export default PDFViewer;
